import { gql } from "@apollo/client";

export const GET_USERS_ASSIGNED_TASKS = gql`
  subscription getAssignedTasks($user_id: uuid!, $todays_date: date!) {
    user_tasks_map(
      where: {
        user_id: { _eq: $user_id }
        _and: {
          task_date: { _gte: $todays_date }
          _and: { task_status: { _eq: pending } }
        }
      }
    ) {
      task {
        task_id
        task_type
        is_custom
        description
      }
      task_date
      user_task_id
      required_attachment
    }
  }
`;

export const GET_AVAILABLE_PREDEFINED_TASKS_FOR_USERS = gql`
  subscription getPredefinedTasks($user_id: uuid!, $filter: String) {
    tasks(
      where: {
        job_role: { users: { user_id: { _eq: $user_id } } }
        _and: {
          is_deleted: { _eq: false }
          _and: { description: { _ilike: $filter } }
        }
      }
    ) {
      task_id
      description
      is_custom
      task_type
      task_json
      taskTypeByTaskType {
        type
        type_name
      }
      user_tasks_maps(where: { user_id: { _eq: $user_id } }) {
        user_task_id
        task_date
        task_status
        required_attachment
      }
    }
  }
`;

export const GET_CUSTOM_TASKS_FOR_USERS = gql`
  subscription getCustomTasks($user_id: uuid!, $filter: String) {
    customTasks: user_tasks_map(
      where: {
        user_id: { _eq: $user_id }
        _and: {
          task: { is_custom: { _eq: true } }
          _and: {
            task: {
              description: { _ilike: $filter }
              is_deleted: { _eq: false }
            }
          }
        }
      }
      distinct_on: [task_id, task_date]
    ) {
      task {
        task_id
        task_type
        is_custom
        description
        taskTypeByTaskType {
          type
          type_name
        }
      }
      task_date
      task_status
      user_task_id
      required_attachment
    }
  }
`;

export const GET_CUSTOM_TASK = gql`
  subscription getCustomTasks($user_id: uuid!) {
    tasks(where: { task_user: { _eq: $user_id }, is_deleted: { _eq: false } }) {
      task_id
      description
      is_custom
      task_type
      taskTypeByTaskType {
        type
        type_name
      }
    }
  }
`;

export const GET_CUSTOM_TASKS_FOR_USERS_PENDING = gql`
  subscription getCustomTasks($user_id: uuid!, $todays_date: date!) {
    customTasks: user_tasks_map(
      where: {
        user_id: { _eq: $user_id }
        _and: {
          task: { is_custom: { _eq: true } }
          _and: {
            task_date: { _gte: $todays_date }
            task_status: { _eq: pending }
          }
        }
      }
      distinct_on: [task_id, task_date]
    ) {
      task {
        task_id
        task_type
        is_custom
        description
        taskTypeByTaskType {
          type
          type_name
        }
      }
      task_date
      task_status
      user_task_id
      required_attachment
    }
  }
`;

export const GET_USER_DETAILS_BY_ID = gql`
  subscription getUsersById($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      first_name
      last_name
      profile_image
      user_location_maps(
        where: { user_id: { _eq: $user_id } }
        distinct_on: business_id
      ) {
        businessDetails {
          brand_icon
          brand_logo
          business_name
        }
      }
      user_access: min_access_level {
        access_id
        access_type
      }
    }
  }
`;

export const GET_USERS_TASKS_LIST = gql`
  subscription user_tasks_map($user_id: uuid, $todaysDate: date) {
    user_tasks_map(
      where: { user_id: { _eq: $user_id }, task_date: { _eq: $todaysDate } }
    ) {
      task {
        description
        task_id
        task_tutorial_maps {
          tutorial {
            tutorial_type
            tutorial_url
          }
        }
        taskType: taskTypeByTaskType {
          type
          type_name
        }
        user_tasks_maps(
          where: { user_id: { _eq: $user_id }, task_date: { _eq: $todaysDate } }
        ) {
          required_attachment
        }
      }
      task_status
      task_date
      user_task_id
    }
  }
`;

export const GET_ANNOUNCEMENT_SUBSCRIPTION = gql`
  subscription getAnnouncementSubscription($user_id: uuid!) {
    announcementData: announcement_user_map(
      where: { user_id: { _eq: $user_id }, is_read: { _eq: false } }
      limit: 1
    ) {
      announcementDetails {
        announcement_id
        announcement_message
      }
    }
  }
`;

export const GET_USER_ESCALATION_DETAILS = gql`
  subscription getEscalatedTasks($user_id: uuid = "", $todaysDate: date = "") {
    user_reporting_view(where: { leader_id: { _eq: $user_id } }) {
      subordinate_details {
        user_tasks_maps(
          where: {
            task_date: { _eq: $todaysDate }
            task_status: { _eq: escalated }
          }
          distinct_on: task_id
        ) {
          task {
            description
          }
          user {
            full_name
            user_location_maps {
              locationDetails {
                location_name
              }
            }
          }
          updated_at
          task_date
          task_status
        }
      }
    }
  }
`;

export const SUBSCRIBE_EMPLOYEE_LIST = gql`
  subscription subscribeEmployeeList(
    $user_id: uuid
    $name: String
    $email: String
    $employee_code: String
    $role: String
    $location: String
  ) {
    employeeList: user_reporting_view(
      where: {
        leader_id: { _eq: $user_id }
        _and: {
          _or: [
            { subordinate_details: { first_name: { _ilike: $name } } }
            { subordinate_details: { last_name: { _ilike: $name } } }
          ]
        }
        subordinate_details: {
          email: { _ilike: $email }
          employee_code: { _ilike: $employee_code }
          user_location_maps: {
            jobRoleDetails: { role_name: { _ilike: $role } }
          }
        }
        location_details: { location_name: { _ilike: $location } }
      }
      distinct_on: subordinate_id
    ) {
      location_details {
        location_name
      }
      employee_details: subordinate_details {
        full_name
        first_name
        last_name
        email
        employee_code
        profile_image
        user_id
        user_location_maps(distinct_on: job_role_id) {
          jobRoleDetails {
            role_id
            role_name
          }
        }
      }
    }
  }
`;

export const GET_DASHBOARD_LOCATION_COUNT = gql`
  subscription getDashboardLocationsCount($user_id: uuid) {
    locations: user_reporting_view(
      where: { leader_id: { _eq: $user_id } }
      distinct_on: location_id
    ) {
      location_details {
        location_name
        location_id
      }
    }
  }
`;

export const GET_DASHBOARD_EMPLOYEE_COUNT = gql`
  subscription getDashboardEmployeesCount(
    $user_id: uuid = "620ed656-307a-4c82-83bc-e556e85e09cf"
  ) {
    employees: user_reporting_view(
      where: { leader_id: { _eq: $user_id } }
      distinct_on: subordinate_id
    ) {
      subordinate_details {
        user_id
        full_name
      }
    }
  }
`;

export const USER_TIMEZONE_SUBSCRIPTION = gql`
  subscription getUserTimezone($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      time_zone
    }
  }
`;

export const USERS_ON_LEAVE_TODAY_SUBSCRIPTION = gql`
  subscription getEmployeesOnLeaveToday($user_id: uuid, $todays_date: date) {
    user_reporting_view(
      where: {
        leader_id: { _eq: $user_id }
        _and: {
          subordinate_details: {
            user_vacations: {
              start_date: { _lte: $todays_date }
              end_date: { _gte: $todays_date }
            }
          }
        }
      }
      distinct_on: subordinate_id
    ) {
      subordinate_details {
        user_vacations {
          vacation_reason
          vacation_id
          user_id
          start_date
          end_date
        }
        full_name
        first_name
        last_name
        profile_image
        user_location_maps {
          jobRoleDetails {
            role_name
          }
          locationDetails {
            location_name
          }
        }
      }
    }
  }
`;

export const GET_DASHBOARD_TODAYS_TASK_COUNT = gql`
  subscription getTodaysTaskCounts($user_id: uuid, $todays_date: date) {
    user_reporting_view(
      where: { leader_id: { _eq: $user_id } }
      distinct_on: subordinate_id
    ) {
      subordinate_details {
        completedTasks: user_tasks_maps_aggregate(
          where: {
            task_status: { _eq: completed }
            task_date: { _eq: $todays_date }
          }
        ) {
          aggregate {
            count
          }
        }
        allTasks: user_tasks_maps_aggregate(
          where: { task_date: { _eq: $todays_date } }
        ) {
          aggregate {
            count
          }
        }
      }
    }
  }
`;
export const GET_LOCATION_LIST = gql`
  subscription getUserLocationMaps(
    $user_id: uuid
    $type_id: String
    $working_day: String
    $state: String
    $city: String
    $country: String
    $zipcode: String
    $location_name: String
  ) {
    getUserLocationMaps(
      where: {
        user_id: { _eq: $user_id }
        locationDetails: {
          state: { _ilike: $state }
          city: { _ilike: $city }
          country: { _ilike: $country }
          zipcode: { _ilike: $zipcode }
          location_name: { _ilike: $location_name }
        }
      }
    ) {
      locationDetails {
        location_name
        state
        street_address
        city
        country
        location_id
        zipcode
        location_timings(where: { working_day: { _eq: $working_day } }) {
          working_day
          opening_time
          closing_time
        }
        user_location_maps(
          where: {
            jobRoleDetails: { accessDetails: { type_id: { _gt: $type_id } } }
          }
        ) {
          userDetails {
            first_name
            last_name
            user_id
          }
        }
      }
    }
  }
`;
export const GET_DASHBOARD_WEEKLY_TASK_COUNT = gql`
  subscription getPrevWeekTaskCounts(
    $user_id: uuid!
    $end_date: date!
    $start_date: date!
  ) {
    user_reporting_view(where: { leader_id: { _eq: $user_id } }) {
      subordinate_details {
        user_tasks_status_by_date_aggregate(
          where: {
            day: { _gte: $start_date }
            _and: { day: { _lte: $end_date } }
          }
        ) {
          aggregate {
            sum {
              completed
              escalated
              incompleted
              pending
            }
            avg {
              completedperc
            }
          }
        }
      }
    }
  }
`;

export const GET_USER_NOTIFICATION = gql`
  subscription notification_users($user_id: uuid) {
    notification_users(
      where: { user_id: { _eq: $user_id }, is_read: { _eq: false } }
      order_by: { updated_at: desc }
    ) {
      is_read
      notification_id
      notification {
        created_at
        description
        rel_type
        title
      }
    }
  }
`;

export const GET_USER_WORKING_SHIFT = gql`
  subscription userShiftTime($working_day: String!, $user_id: uuid!) {
    user_shift_by_pk(user_id: $user_id, working_day: $working_day) {
      start_time
      end_time
    }
  }
`;

export const GET_USER_SUBSCRIPTION = gql`
  subscription getSubscriptionDetails($user_id: uuid!) {
    user_subscription(where: { user_id: { _eq: $user_id } }) {
      next_invoice_due
      subscriptionType {
        type
        description
      }
      subscriptionStatus {
        status
        description
      }
      locations
      stripe_price_id
      stripe_customer_id
      subscription_transactions(order_by: { createdAt: desc_nulls_last }) {
        amount_paid
        invoice_number
        invoice_pdf
        payment_status
        createdAt
      }
      package_price {
        price
        free_trial_days
        package {
          name
        }
      }
    }
  }
`;

export const GET_USER_TASK_COUNT = gql`
  query getLocationPerformanceReport(
    $business_id: uuid!
    $filter: String
    $type_id: String
    $location_id: [uuid!]
  ) {
    getLocationPerformanceReport(
      args: { businessid: $business_id, filter: $filter, typeid: $type_id }
      where: { location_id: { _in: $location_id } }
    ) {
      business_id
      business_name
      completed
      count
      escalated
      location_id
      location_name
      users
    }
  }
`;

export const GET_EMPLOYEE_PERFORMANCE = gql`
  query getEmployeePerformanceReport(
    $filter: String
    $current_user: [uuid!]
    $user_id: uuid!
    $locations: [uuid!]
  ) {
    getEmployeePerformanceReport(
      args: { filter: $filter, userid: $user_id }
      where: {
        user_id: { _in: $current_user }
        _or: { location: { location_id: { _in: $locations } } }
      }
    ) {
      assigned
      completed
      escalated
      incomplete
      user_id
      user_name
    }
  }
`;

export const GET_OWNER_LOCATION_COUNT = gql`
  subscription getDashboardLocationsCount($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      user_location_maps(where: { user_id: { _eq: $user_id } }) {
        locationDetails {
          location_id
          location_name
        }
      }
    }
  }
`;

export const GET_USER_LOCATION_LIST = gql`
  subscription getUserLocationList($user_id: uuid!) {
    getUsersById(user_id: $user_id) {
      user_location_maps {
        locationDetails {
          location_name
          location_id
        }
      }
    }
  }
`;

export const GET_USER_TODAY_TASK_STATUS = gql`
  subscription getUserTodayTaskStatus($user_id: uuid!, $task_date: date) {
    user_tasks_map(
      where: {
        task_date: { _eq: $task_date }
        task_status: { _neq: pending }
        _or: [{user_id: {_eq: $user_id}}, {task_status_updated_by: {_eq: $user_id}}]
      }
    ) {
      assigned_by
      task_status
      task_id
      task_date
      user_id
      task {
        description
        task_type
        taskTypeByTaskType {
          type_name
        }
      }
      user_task_id
    }
  }
`;
